import { Skeleton, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Image as ImageIcon, Pencil } from "lucide-react";
import { useEffect, useState } from "react";

import { useGetTeamBalance } from "../../../api/generated/endpoints/billing/billing";
import { getGetJobQueryKey, useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { Catalog, Job, OutputRef } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { GENERATIONS_PAGE, useJobList } from "../../../platform/jobs/job-list";
import { useStreamStatus } from "../../../platform/stream-status";
import { PromptBox } from "../../../platform/studio/composer/PromptBox";
import { ComposerErrors, PriceAndStart } from "../../../platform/studio/composer/Start";
import { useComposer } from "../../../platform/studio/composer/use-composer";
import { CardPicture } from "../../../ui/CardPicture";
import { formatCount } from "../../../ui/format";
import home from "../pro-home.module.css";
import { SimpleResult } from "./SimpleResult";
import local from "./simple-fix.module.css";
import flow from "./simple-home.module.css";
import classes from "./simple.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/simple/fix-image";
const NO_SHAPE = () => undefined;
const EXACT = { exact: true };

/** The prototype's quick fixes (`imgfix`), in its order and words: a fix's line is its prompt. */
export const FIXES = [
  { name: "Brighter", line: "Brighten the image and adjust colors to make the product stand out" },
  { name: "Plain background", line: "Clean, plain background" },
  { name: "Bigger product", line: "Make the product bigger in the frame" },
  { name: "Golden hour", line: "Warmer, golden-hour light" },
  { name: "No text", line: "Remove the text" },
  { name: "Soft shadow", line: "Add a soft shadow under the product" },
] as const;

interface Made {
  ref: OutputRef;
  picture: string;
  prompt: string;
}

/** A job's delivered pictures, each a source a tweak can change; a Layerize's are layers, not pictures. */
function madeOf(job: Job): Made[] {
  if (job.status !== "succeeded" || job.type === "image.layerize") return [];
  return job.outputs.flatMap((output) =>
    output.url === null
      ? []
      : [
          {
            ref: { kind: "job_output", job_id: job.id, output_index: output.index },
            picture: output.thumb_url ?? output.url,
            prompt: job.params?.prompt ?? "",
          },
        ],
  );
}

const same = (a: OutputRef | null, b: OutputRef) => a?.job_id === b.job_id && a.output_index === b.output_index;

/** Step 1: one of the brand's images, the Images page's own read (its recent images, 5 a page). */
function PickImage(props: { teamId: string; brandId: string; picked: OutputRef | null; onPick: (ref: OutputRef) => void }) {
  const { teamId, brandId, picked, onPick } = props;
  const jobs = useJobList(teamId, { status: "all", brandId, medium: "image", pageSize: GENERATIONS_PAGE }, useStreamStatus());
  const made = jobs.data?.pages.flatMap((page) => page.items.flatMap(madeOf));
  // A picture that fails keeps its place, unseen under the image icon, until it loads after all:
  // the media cookies' retry asks again for that very element, and the list is read again.
  const lost = (
    <span className={flow.artIcon}>
      <ImageIcon size={24} strokeWidth={1.5} aria-hidden />
    </span>
  );
  return (
    <section className={classes.step} aria-labelledby="simple-fix-image">
      <h2 id="simple-fix-image" className={classes.stepTitle}>
        Pick the image
      </h2>
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {made === undefined ? (
        jobs.isError ? null : (
          <div className={classes.products} aria-hidden>
            {[0, 1, 2, 3].map((each) => (
              <Skeleton key={each} className={classes.productSkeleton} />
            ))}
          </div>
        )
      ) : made.length === 0 && !jobs.hasNextPage ? (
        <p className={classes.muted}>
          No images yet.{" "}
          <Link to="/teams/$teamId/brands/$brandId/simple/make" params={{ teamId, brandId }} search={{ kind: "image" }}>
            Make one
          </Link>{" "}
          first, then fix it here.
        </p>
      ) : (
        <>
          <ul className={classes.products} aria-label="Your images">
            {made.map((each, index) => (
              <li key={`${each.ref.job_id} ${each.ref.output_index}`}>
                <button
                  type="button"
                  className={classes.product}
                  aria-pressed={same(picked, each.ref)}
                  aria-label={`Image ${index + 1}`}
                  title={each.prompt}
                  onClick={() => onPick(each.ref)}
                >
                  <span className={`${classes.productPicture} ${local.picture}`}>
                    <CardPicture src={each.picture} alt="" onFailed={() => void jobs.refetch()} fallback={lost} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {jobs.hasNextPage ? (
            <button
              type="button"
              className={home.smallButton}
              disabled={jobs.isFetchingNextPage}
              onClick={() => void jobs.fetchNextPage()}
            >
              Show more
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}

interface FixProps {
  teamId: string;
  brandId: string;
  catalog: Catalog;
  source: OutputRef;
}

/**
 * Step 2 and the run: a quick fix, or the person's own words, on the Studio's own composer, live's
 * Tweak of the picked image (`image.tweak`, `edit: "tweak"`), at the catalog's default model and
 * one output, the cheapest run; then the result, as the guided page shows one.
 */
function Fix({ teamId, brandId, catalog, source }: FixProps) {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [fix, setFix] = useState<string | null>(FIXES[0].name);
  const c = useComposer({
    catalog,
    medium: "image",
    initialSource: source,
    initialType: "image.tweak",
    clone: undefined,
    onStarted: (job) => {
      // The result reads the job the start answered at once; the stream keeps it current.
      queryClient.setQueryData(getGetJobQueryKey(teamId, job.id), job);
      setJobId(job.id);
    },
    onShapeChange: NO_SHAPE,
  });
  const { set } = c;
  const outputs = c.draft.outputs;
  const prompt = c.draft.prompt;
  const line = FIXES.find((each) => each.name === fix)?.line;
  useEffect(() => {
    if (outputs !== 1) set({ outputs: 1 });
    // A quick fix's line is the prompt; with the person's own words, the prompt is theirs.
    if (line !== undefined && prompt !== line) set({ prompt: line });
  }, [outputs, line, prompt, set]);
  const balance = useGetTeamBalance(teamId).data;
  return (
    <>
      <section className={classes.step} aria-labelledby="simple-fix-fix">
        <h2 id="simple-fix-fix" className={classes.stepTitle}>
          Pick a fix
        </h2>
        <fieldset className={`${classes.kinds} ${local.fixes}`} aria-label="Quick fixes">
          {FIXES.map((each, index) => (
            <button
              key={each.name}
              type="button"
              className={classes.kind}
              aria-pressed={fix === each.name}
              onClick={() => setFix(each.name)}
            >
              <span className={classes.kindTitle}>
                {each.name}
                {index === 0 ? <span className={classes.optional}>Recommended</span> : null}
              </span>
              <span className={classes.kindLine}>{each.line}</span>
            </button>
          ))}
        </fieldset>
        <div className={classes.card}>
          {fix === null ? (
            <div className={classes.prompt}>
              <PromptBox c={c} />
            </div>
          ) : (
            <div className={classes.row}>
              <UnstyledButton
                className={classes.chip}
                onClick={() => {
                  setFix(null);
                  set({ prompt: "" });
                }}
              >
                <Pencil size={14} aria-hidden /> Write my own
              </UnstyledButton>
            </div>
          )}
          <div className={classes.gobar}>
            {balance === undefined ? null : (
              <span className={classes.balance}>Balance: {formatCount(balance.available)} credits</span>
            )}
            <span className={classes.go}>
              <PriceAndStart c={c} />
            </span>
          </div>
          <ComposerErrors c={c} />
        </div>
        {c.dialog}
      </section>
      {jobId === null ? null : (
        <SimpleResult
          teamId={teamId}
          brandId={brandId}
          jobId={jobId}
          onAgain={c.startJob}
          onAnother={() => setJobId(null)}
        />
      )}
    </>
  );
}

/**
 * Images' Fix this image (spec ui-perf/simple-fix-image), the Created AI Simple prototype's "Small
 * new" wizard: the image, then the fix, then live's Tweak of it, one output.
 */
export function SimpleFix() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const catalog = useGetCatalog();
  const [picked, setPicked] = useState<OutputRef | null>(null);
  const offered = catalog.data?.image_models.some((each) => each.job_types.includes("image.tweak"));
  return (
    <div className={classes.page}>
      <div className={classes.step}>
        <Link
          to="/teams/$teamId/brands/$brandId/simple/images"
          params={{ teamId, brandId }}
          activeOptions={EXACT}
          className={`${home.subtle} ${flow.back}`}
        >
          <ArrowLeft size={14} aria-hidden /> Images
        </Link>
        <h1 className={classes.title}>Fix this image</h1>
        <p className={classes.muted}>Small changes to an image you made.</p>
      </div>
      <PickImage teamId={teamId} brandId={brandId} picked={picked} onPick={setPicked} />
      {picked === null ? null : catalog.isError ? (
        <ErrorAlert error={catalog.error} />
      ) : catalog.data === undefined ? (
        <Skeleton className={classes.skeleton} />
      ) : offered === true ? (
        // Another image starts the fix anew: its own draft, price and result.
        <Fix
          key={`${brandId} ${picked.job_id} ${picked.output_index}`}
          teamId={teamId}
          brandId={brandId}
          catalog={catalog.data}
          source={picked}
        />
      ) : (
        <p className={classes.muted}>No model makes this yet.</p>
      )}
    </div>
  );
}
